import { useCallback, useEffect, useState } from 'react'
import { Check, Trash2, X } from 'lucide-react'
import type { ServerApiClient } from '../../../../data/ServerApiClient'
import { CfoAnalysisChart } from './CfoAnalysisStream'
import type { CfoMonthlyAnalysisReport, CfoReportAnalysisItem } from './types'
import './CfoMonthlyReportPanel.css'

const trimStr = (value: unknown): string => String(value ?? '').trim()

const emptyReport = (workbookId: string, period: string): CfoMonthlyAnalysisReport => ({
  workbook_id: workbookId,
  period,
  title: `Analysis report · ${period}`,
  status: 'draft',
  summary: '',
  sections: [],
  analysis_items: [],
})

export type CfoMonthlyReportPanelProps = {
  appApi: ServerApiClient
  workbookId: string | null
  period: string
  onClose: () => void
}

export function CfoMonthlyReportPanel({ appApi, workbookId, period, onClose }: CfoMonthlyReportPanelProps) {
  const [report, setReport] = useState<CfoMonthlyAnalysisReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    let cancelled = false
    const wb = trimStr(workbookId)
    if (!wb) {
      setLoading(false)
      setError('No workbook is bound to this report.')
      return
    }
    setLoading(true)
    setError('')
    appApi
      .getQuickBooksCfoMonthlyAnalysisReport(wb, period)
      .then((resp: any) => {
        if (cancelled) return
        setReport({ ...emptyReport(wb, period), ...(resp || {}), analysis_items: Array.isArray(resp?.analysis_items) ? resp.analysis_items : [] })
      })
      .catch((err: any) => {
        if (cancelled) return
        setReport(emptyReport(wb, period))
        setError(trimStr(err?.message || err) || 'Could not load this month’s report.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appApi, period, workbookId])

  const patchItem = useCallback((id: string, patch: Partial<CfoReportAnalysisItem>) => {
    setReport((prev) =>
      prev ? { ...prev, analysis_items: prev.analysis_items.map((it) => (it.id === id ? { ...it, ...patch } : it)) } : prev,
    )
    setSaved(false)
  }, [])

  const removeItem = useCallback((id: string) => {
    setReport((prev) => (prev ? { ...prev, analysis_items: prev.analysis_items.filter((it) => it.id !== id) } : prev))
    setSaved(false)
  }, [])

  const save = useCallback(async () => {
    const wb = trimStr(workbookId)
    if (!wb || !report || saving) return
    setSaving(true)
    setError('')
    try {
      const resp: any = await appApi.updateQuickBooksCfoMonthlyAnalysisReport(wb, { report })
      setReport({ ...report, ...(resp || {}), analysis_items: Array.isArray(resp?.analysis_items) ? resp.analysis_items : report.analysis_items })
      setSaved(true)
    } catch (err: any) {
      setError(trimStr(err?.message || err) || 'Could not save the report.')
    } finally {
      setSaving(false)
    }
  }, [appApi, report, saving, workbookId])

  const items = report?.analysis_items || []

  return (
    <div className="cfo-report-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <aside className="cfo-report-panel" role="dialog" aria-modal="true" aria-label="Monthly analysis report">
        <header className="cfo-report-head">
          <div>
            <div className="cfo-report-eyebrow">Analysis report</div>
            <input
              className="cfo-report-title"
              value={report?.title || ''}
              placeholder={`Analysis report · ${period}`}
              onChange={(e) => {
                setReport((prev) => (prev ? { ...prev, title: e.target.value } : prev))
                setSaved(false)
              }}
              aria-label="Report title"
            />
          </div>
          <div className="cfo-report-head-actions">
            <button type="button" className="cfo-report-save" onClick={() => void save()} disabled={saving || !report}>
              {saved ? (
                <>
                  <Check size={14} /> Saved
                </>
              ) : saving ? (
                'Saving…'
              ) : (
                'Save draft'
              )}
            </button>
            <button type="button" className="cfo-report-iconbtn" aria-label="Close" onClick={onClose}>
              <X size={16} />
            </button>
          </div>
        </header>

        {error ? <div className="cfo-report-error">{error}</div> : null}

        <div className="cfo-report-body">
          {loading ? (
            <p className="cfo-report-empty">Loading this month’s report…</p>
          ) : items.length === 0 ? (
            <p className="cfo-report-empty">
              Nothing tagged for {period} yet. Open the analysis modal on any number and tap{' '}
              <b>“Tag to this month’s report.”</b>
            </p>
          ) : (
            items.map((item) => (
              <article className="cfo-report-item" key={item.id}>
                <div className="cfo-report-item-head">
                  <input
                    className="cfo-report-item-title"
                    value={item.title}
                    onChange={(e) => patchItem(item.id, { title: e.target.value })}
                    aria-label="Analysis title"
                  />
                  <button
                    type="button"
                    className="cfo-report-iconbtn"
                    aria-label="Remove analysis"
                    onClick={() => removeItem(item.id)}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
                {item.metric_label || item.period ? (
                  <div className="cfo-report-item-meta">
                    {[item.metric_label, item.period].filter(Boolean).join(' · ')}
                  </div>
                ) : null}
                {item.narrative ? <p className="cfo-report-item-narrative">{item.narrative}</p> : null}
                {item.visual && item.visual.kind === 'chart' ? (
                  <CfoAnalysisChart chart={item.visual.chart} onPrompt={() => {}} disabled />
                ) : null}
                {item.visual && item.visual.kind === 'value' ? (
                  <div className="cfo-report-item-value">
                    <span>{item.visual.label}</span>
                    <b>{item.visual.display}</b>
                  </div>
                ) : null}
                {item.visual && item.visual.kind === 'ratio_health' ? (
                  <div className="cfo-report-item-value">
                    <span>{item.visual.label}</span>
                    <b>{item.visual.display}</b>
                  </div>
                ) : null}
                <textarea
                  className="cfo-report-item-note"
                  value={item.note || ''}
                  placeholder="Add your commentary…"
                  rows={2}
                  onChange={(e) => patchItem(item.id, { note: e.target.value })}
                  aria-label="Your commentary"
                />
              </article>
            ))
          )}
        </div>
      </aside>
    </div>
  )
}

export default CfoMonthlyReportPanel
